import { useCallback, useEffect, useRef, useState } from 'react';
import { api, type VisitorCounts } from '../api.ts';
import { createSocket, type AppSocket } from '../socket.ts';

export type ConnState = 'connecting' | 'open' | 'closed';

export interface AuthUser { id: number; username: string }

export interface AppState {
  user: AuthUser | null;
  online: number;
  visitors: VisitorCounts;
  version: string;
  conn: ConnState;
  lastError: string | null;
}

export function useApp(): {
  state: AppState;
  refreshMe: () => Promise<void>;
  setUser: (u: AuthUser | null) => void;
} {
  const [state, setState] = useState<AppState>({
    user: null,
    online: 1,
    visitors: { total: 0, weekly: 0 },
    version: '0.0.0',
    conn: 'connecting',
    lastError: null,
  });
  const sockRef = useRef<AppSocket | null>(null);

  const refreshMe = useCallback(async (): Promise<void> => {
    try {
      const me = await api.me();
      setState(s => ({ ...s, user: me.user, online: me.online, visitors: me.visitors, version: me.version }));
    } catch (err) {
      setState(s => ({ ...s, lastError: err instanceof Error ? err.message : 'unknown error' }));
    }
  }, []);

  const setUser = useCallback((u: AuthUser | null) => {
    setState(s => ({ ...s, user: u }));
  }, []);

  useEffect(() => {
    refreshMe();

    const sock = createSocket();
    sockRef.current = sock;

    sock.on('connect', () => setState(s => ({ ...s, conn: 'open' })));
    sock.on('disconnect', () => setState(s => ({ ...s, conn: 'closed' })));
    sock.on('connect_error', () => setState(s => ({ ...s, conn: 'closed' })));
    sock.on('presence:update', (n: number) => setState(s => ({ ...s, online: n })));

    // Re-poke the socket on visibility/network events — socket.io's automatic
    // reconnection sometimes stalls in background tabs.
    const onWake = (): void => {
      if (!sock.connected) sock.connect();
    };
    document.addEventListener('visibilitychange', onWake);
    window.addEventListener('focus', onWake);
    window.addEventListener('online', onWake);

    return () => {
      document.removeEventListener('visibilitychange', onWake);
      window.removeEventListener('focus', onWake);
      window.removeEventListener('online', onWake);
      sock.disconnect();
      sockRef.current = null;
    };
  }, [refreshMe]);

  return { state, refreshMe, setUser };
}
